import { refusedCardKeysIn } from "./card-projection-refusal";

/**
 * THE SERVING-UNCONFIRMED COPY: what was not confirmed about an app the control
 * plane holds live, and what happens next — one sentence per cause, written once.
 *
 * An app is "serving unconfirmed" when its deployment is HEALTHY to the control
 * plane and the tenant's cluster does not confirm it (`agentConfirmsServing`).
 * The cluster's last report is the only evidence of WHY, and since the agent
 * reports a placed app that fails after its rollout as `ROLLED_BACK`, the
 * reasons no longer share one consequence: a refused placement leaves the last
 * version serving, while an app failing live is down for its visitors. One
 * sentence for both was wrong for one of them.
 *
 * Every surface that names this state renders it from here, so they cannot
 * disagree: the tenant alert's Apps field and Detail
 * (`serving-deployment-unconfirmed.detector.ts`), the issue registry's message
 * and remedy (`VIBE_ALERT_SERVING_DEPLOYMENT_UNCONFIRMED`), and the pre-roll
 * census line (`describeBlockingApp`).
 *
 * The words are for a reader who knows apps, versions and credentials, and
 * nothing about schedulers: no job, allocation, color or probe appears in them.
 */

/** Why a live app is not confirmed, read from the cluster's last report on it. */
export const SERVING_UNCONFIRMED_CAUSES = [
  "never-reported",
  "credential-refused",
  "still-starting",
  "failing-live",
  "stopped",
  "unrecognised"
] as const;

export type ServingUnconfirmedCause = (typeof SERVING_UNCONFIRMED_CAUSES)[number];

/** One cause's two halves. Each is a whole sentence, so either can stand alone. */
export interface ServingUnconfirmedCopy {
  /** What was not confirmed. */
  readonly notConfirmed: string;
  /** What happens next, or what the reader can do. */
  readonly next: string;
}

export const SERVING_UNCONFIRMED_COPY: Readonly<
  Record<ServingUnconfirmedCause, ServingUnconfirmedCopy>
> = {
  "never-reported": {
    notConfirmed: "The cluster has never confirmed that this app is running.",
    next: "No new version of it can go live until the cluster reports on it. If that does not happen on its own, check that the cluster is online."
  },
  "credential-refused": {
    notConfirmed:
      "Its latest version was not started, because a credential it needs could not be handed to it.",
    next: "The previous version keeps serving meanwhile. Fix the credential the cluster's reason names — reconnect or approve it — and the latest version starts on its own."
  },
  "still-starting": {
    notConfirmed: "Its latest version has not finished starting.",
    next: "The previous version keeps serving meanwhile. If it still does not finish, the cluster's reason says what it is waiting for."
  },
  "failing-live": {
    notConfirmed:
      "The app started, but it is not answering its health check, or it keeps restarting.",
    next: "Visitors may be getting errors now. Read the cluster's reason, then fix the app and deploy again, or roll back to a version that worked."
  },
  stopped: {
    notConfirmed: "The app stopped: it crashed repeatedly, or the cluster could not start it.",
    next: "It is not serving. Read the cluster's reason, fix the cause and deploy again."
  },
  unrecognised: {
    notConfirmed: "The cluster sent a status this version of the platform does not recognise.",
    next: "Treat the app as not working until the cluster reports it healthy, and read the cluster's reason for what it sent."
  }
};

/**
 * The cluster statuses (`VibeAgentReportedDeploymentStatus`) that do not confirm
 * a live app, each with its cause. `HEALTHY` is absent because it confirms;
 * a crash loop is stored as `FAILED`, beside a reconcile fault, so `stopped`
 * covers both.
 */
const CAUSE_BY_STATUS: Readonly<Record<string, ServingUnconfirmedCause>> = {
  PENDING: "still-starting",
  DEPLOYING: "still-starting",
  ROLLED_BACK: "failing-live",
  FAILED: "stopped"
};

/**
 * The cause of one unconfirmed app. Total: a status this build does not list is
 * `unrecognised`, never folded into a cause whose next step it may not share.
 * A `PENDING` or `DEPLOYING` report naming a refused credential is the agent
 * declining to start the version at all, so it is `credential-refused`.
 */
export function servingUnconfirmedCauseOf(app: {
  readonly agentStatus: string | null;
  readonly agentDetail: string | null;
}): ServingUnconfirmedCause {
  if (app.agentStatus === null) return "never-reported";
  const cause = Object.prototype.hasOwnProperty.call(CAUSE_BY_STATUS, app.agentStatus)
    ? CAUSE_BY_STATUS[app.agentStatus]
    : undefined;
  if (cause === undefined) return "unrecognised";
  if (cause === "still-starting" && refusedCardKeysIn(app.agentDetail).length > 0) {
    return "credential-refused";
  }
  return cause;
}

/** Both halves of one cause, as the one sentence pair every surface prints. */
export function describeServingUnconfirmedCause(cause: ServingUnconfirmedCause): string {
  const copy = SERVING_UNCONFIRMED_COPY[cause];
  return `${copy.notConfirmed} ${copy.next}`;
}

/** The registry message's tail: every cause's first half, in order. */
export function servingUnconfirmedCausesSentence(): string {
  return SERVING_UNCONFIRMED_CAUSES.map(
    (cause) => SERVING_UNCONFIRMED_COPY[cause].notConfirmed
  ).join(" Or: ");
}

/** The registry remedy's body: every cause, each with its own next step, in order. */
export function servingUnconfirmedNextStepsSentence(): string {
  return SERVING_UNCONFIRMED_CAUSES.map(describeServingUnconfirmedCause).join(" ");
}
